'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Typography } from '@shared/ui/typography/Typography';
import { PrimaryButton } from '@shared/ui/button/PrimaryButton';
import { Icon } from '@shared/ui/icons';
import { resendVerificationEmail } from '@entities/user/api/auth.api';
import { useAuthStore } from '@shared/lib/store/auth-store';
import { parseVerificationError, secondsToMinutes } from '@shared/lib/utils/error-parser';

const REAPPEAR_DELAY = 2 * 60 * 1000; // 2 minutes in milliseconds

/**
 * Verification Banner
 * Floating notification in bottom-right corner for unverified users
 * Can be temporarily dismissed, reappears after 2 minutes
 */
export function VerificationBanner() {
  const t = useTranslations('auth.verificationBanner');
  const tErrors = useTranslations('auth.login.errors');
  const { user } = useAuthStore();
  const [isResending, setIsResending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Only show for authenticated, unverified users
  const shouldShow = user && !user.isVerified;

  // Handler for closing the banner
  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  // Watch for verification changes and trigger closing animation
  useEffect(() => {
    if (user?.isVerified && isVisible && !isClosing) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isVerified]);

  // Initial mount animation
  useEffect(() => {
    if (shouldShow) {
      const timer = setTimeout(() => {
        setIsInitialMount(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldShow]);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (shouldShow && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setRetryAfter(null);
            setError(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [countdown, shouldShow]);

  // Reappear timer after dismissal (but not if user got verified)
  useEffect(() => {
    if (shouldShow && !isVisible && !user?.isVerified) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsClosing(false);
      }, REAPPEAR_DELAY);

      return () => clearTimeout(timer);
    }
  }, [isVisible, shouldShow, user?.isVerified]);

  // Early return - don't render anything if conditions not met
  if (!shouldShow || !isVisible) {
    return null;
  }

  const handleResend = async () => {
    if (retryAfter) {
      return; // Prevent resend during rate limit
    }

    setIsResending(true);
    setError(null);
    setShowSuccess(false);

    try {
      await resendVerificationEmail(user.email);
      setShowSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (err) {
      const parsedError = parseVerificationError(err);

      if (parsedError.type === 'rate_limit' && parsedError.retryAfter) {
        const minutes = secondsToMinutes(parsedError.retryAfter);
        setError(tErrors('resendLimitReached', { minutes }));
        setRetryAfter(parsedError.retryAfter);
        setCountdown(parsedError.retryAfter);
      } else {
        setError(t('errors.resendFailed'));
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div
      className={`
        fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60]
        max-w-[calc(100vw-2rem)] sm:max-w-md w-full sm:w-auto
        bg-background-card border-2 border-accent-primary
        rounded-xl shadow-elevated
        transition-all duration-300 ease-out
        ${isInitialMount ? 'opacity-0 translate-y-4 scale-95' : ''}
        ${isClosing ? 'opacity-0 translate-y-2 scale-95' : !isInitialMount ? 'opacity-100 translate-y-0 scale-100' : ''}
      `}
    >
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="absolute top-3 right-3 p-1 rounded-lg hover:bg-surface-hover transition-colors"
        aria-label="Close notification"
      >
        <Icon name="x" size="sm" className="text-text-secondary" />
      </button>

      <div className="p-4 pr-10">
        {/* Header with Icon */}
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 mt-0.5">
            <Icon name="claim" size="md" className="text-accent-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <Typography variant="bodySm" weight="semibold" className="text-text-primary mb-1">
              {t('title')}
            </Typography>
            <Typography variant="note" color="secondary" className="break-words">
              {t('description')}{' '}
              <span className="font-semibold text-text-primary">{user.email}</span>
            </Typography>
          </div>
        </div>

        {/* Status Messages */}
        {showSuccess && (
          <div className="mb-3 p-2 bg-feedback-success/10 rounded-lg">
            <Typography variant="note" className="text-feedback-success">
              {t('successMessage')}
            </Typography>
          </div>
        )}
        {error && (
          <div className="mb-3 p-2 bg-feedback-error/10 rounded-lg">
            <Typography variant="note" className="text-feedback-error">
              {error}
            </Typography>
          </div>
        )}

        {/* Resend Button */}
        <PrimaryButton
          onClick={handleResend}
          disabled={isResending || showSuccess || !!retryAfter}
          fullWidth
          className="justify-center"
        >
          {isResending
            ? t('sending')
            : retryAfter && countdown > 0
            ? `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}`
            : t('resendButton')}
        </PrimaryButton>
      </div>
    </div>
  );
}
